/**
 * Browser Compatibility Check
 * Validates browser support for required AR features
 */

/**
 * Check if required APIs are available
 * @returns {object} - Compatibility results
 */
export function checkBrowserCompatibility() {
  const results = {
    isCompatible: true,
    features: {},
    warnings: [],
    errors: [],
  };

  // Check getUserMedia (camera access)
  results.features.getUserMedia = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  if (!results.features.getUserMedia) {
    results.errors.push('Camera API (getUserMedia) not supported');
    results.isCompatible = false;
  }

  // Check WebGL (required for Three.js/MindAR)
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    results.features.webgl = !!gl;
    if (!results.features.webgl) {
      results.errors.push('WebGL not supported');
      results.isCompatible = false;
    }
  } catch (e) {
    results.features.webgl = false;
    results.errors.push('WebGL not supported');
    results.isCompatible = false;
  }

  // Check IndexedDB (for caching)
  results.features.indexedDB = !!window.indexedDB;
  if (!results.features.indexedDB) {
    results.warnings.push('IndexedDB not available - caching disabled');
  }

  // Check requestAnimationFrame (for render loop)
  results.features.requestAnimationFrame = !!window.requestAnimationFrame;
  if (!results.features.requestAnimationFrame) {
    results.errors.push('requestAnimationFrame not supported');
    results.isCompatible = false;
  }

  // Check HTTPS (required for camera access)
  results.features.https =
    window.location.protocol === 'https:' || window.location.hostname === 'localhost';
  if (!results.features.https) {
    results.errors.push('HTTPS required for camera access (or use localhost)');
    results.isCompatible = false;
  }

  // Browser detection
  const ua = navigator.userAgent;
  results.browser = {
    isChrome: /Chrome/.test(ua) && !/Edge/.test(ua),
    isFirefox: /Firefox/.test(ua),
    isSafari: /Safari/.test(ua) && !/Chrome/.test(ua),
    isEdge: /Edge/.test(ua),
    isIOS: /iPhone|iPad|iPod/.test(ua),
    isAndroid: /Android/.test(ua),
  };

  // Browser-specific warnings
  if (results.browser.isSafari || results.browser.isIOS) {
    results.warnings.push('iOS/Safari may have limited AR performance - Chrome recommended');
  }

  // Mobile detection
  results.isMobile = results.browser.isIOS || results.browser.isAndroid || /Mobile/.test(ua);

  return results;
}

/**
 * Get user-friendly browser name
 * @returns {string} - Browser name
 */
export function getBrowserName() {
  const ua = navigator.userAgent;

  if (/Chrome/.test(ua) && !/Edge/.test(ua)) return 'Chrome';
  if (/Firefox/.test(ua)) return 'Firefox';
  if (/Safari/.test(ua) && !/Chrome/.test(ua)) return 'Safari';
  if (/Edge/.test(ua)) return 'Edge';
  if (/MSIE|Trident/.test(ua)) return 'Internet Explorer';

  return 'Unknown Browser';
}

/**
 * Display compatibility error UI
 * @param {object} results - Compatibility check results
 * @param {HTMLElement} container - Container element
 */
export function displayCompatibilityError(results, container) {
  const errorDiv = document.createElement('div');
  errorDiv.className = 'compatibility-error';

  const browserName = getBrowserName();
  const isMobile = results.isMobile ? 'Mobile' : 'Desktop';

  errorDiv.innerHTML = `
    <div class="compatibility-content">
      <div class="compatibility-icon">⚠️</div>
      <h2 class="compatibility-title">Browser Not Supported</h2>
      <p class="compatibility-description">
        Your browser (${browserName} - ${isMobile}) does not support all required features for AR experience.
      </p>

      ${
        results.errors.length > 0
          ? `
        <div class="compatibility-errors">
          <strong>Missing features:</strong>
          <ul>
            ${results.errors.map((err) => `<li>${err}</li>`).join('')}
          </ul>
        </div>
      `
          : ''
      }

      ${
        results.warnings.length > 0
          ? `
        <div class="compatibility-warnings">
          <strong>Warnings:</strong>
          <ul>
            ${results.warnings.map((warn) => `<li>${warn}</li>`).join('')}
          </ul>
        </div>
      `
          : ''
      }

      <div class="compatibility-recommendation">
        <strong>Recommended:</strong>
        <ul>
          <li>Use Chrome or Firefox on desktop</li>
          <li>Use Chrome on Android mobile</li>
          <li>Use Safari on iOS (with limitations)</li>
          <li>Ensure HTTPS connection</li>
        </ul>
      </div>
    </div>
  `;

  container.appendChild(errorDiv);
}

/**
 * Log compatibility information
 * @param {object} results - Compatibility check results
 */
export function logCompatibilityInfo(results) {
  console.group('🔍 Browser Compatibility Check');
  console.log('Browser:', getBrowserName());
  console.log('Mobile:', results.isMobile ? 'Yes' : 'No');
  console.log('Compatible:', results.isCompatible ? '✅' : '❌');
  console.log('Features:', results.features);

  if (results.warnings.length > 0) {
    console.warn('Warnings:', results.warnings);
  }

  if (results.errors.length > 0) {
    console.error('Errors:', results.errors);
  }

  console.groupEnd();
}
